import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Building2, ArrowLeft, ChevronRight, ArrowRight, RotateCw } from "lucide-react";
import { Logo } from "../components/Logo";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { api, errorMessage } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { LanguageToggle } from "../components/LanguageToggle";

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // email -> password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [orgChoices, setOrgChoices] = useState(null);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  // «Send ny kode»: nedtelling (sekunder) før knappen kan brukes igjen – serveren tillater 3 koder per 15 min
  const RESEND_WAIT = 60;
  const [resendWait, setResendWait] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendWait <= 0) return undefined;
    const timer = setTimeout(() => setResendWait((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendWait]);

  const startForgotPassword = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      toast.success(t(data.message || "Tilbakestillingskode er sendt til e-posten din"));
      setResetCode("");
      setNewPassword("");
      setResendWait(RESEND_WAIT);
      setStep("reset");
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke sende tilbakestillingskoden")));
    } finally {
      setSubmitting(false);
    }
  };

  const resendResetCode = async () => {
    if (resendWait > 0 || resending) return;
    setResending(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      toast.success(t("Ny kode er sendt – sjekk innboksen (og søppelpost). Den forrige koden gjelder ikke lenger."));
      setResetCode("");
      setResendWait(RESEND_WAIT);
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke sende ny kode")));
    } finally {
      setResending(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        email: email.trim(),
        code: resetCode.trim(),
        new_password: newPassword,
      });
      toast.success(t(data.message || "Passordet er oppdatert – logg inn med det nye passordet"));
      setPassword("");
      setStep("password");
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke oppdatere passordet")));
    } finally {
      setSubmitting(false);
    }
  };

  const finishLogin = () => {
    toast.success(t("Velkommen tilbake!"));
    navigate("/dashboard");
  };

  const handleEmailContinue = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/check-email", { email: email.trim() });
      if (data.exists) {
        setStep("password");
      } else {
        toast.info(t("Vi fant ingen konto med denne e-posten – opprett en ny arrangørkonto."));
        navigate(`/registrer?epost=${encodeURIComponent(email.trim())}`);
      }
    } catch (err) {
      toast.error(errorMessage(err, t("Noe gikk galt – prøv igjen")));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.multiple) {
        setOrgChoices(result.organizations);
      } else {
        finishLogin();
      }
    } catch (err) {
      toast.error(errorMessage(err, t("Feil e-post eller passord")));
    } finally {
      setSubmitting(false);
    }
  };

  const chooseOrg = async (organizerId) => {
    setSubmitting(true);
    try {
      await login(email.trim(), password, organizerId);
      finishLogin();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background hero-decor flex flex-col">
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="login-logo-link">
          <Logo showTagline />
        </Link>
        <LanguageToggle />
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md border-border shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">
              {orgChoices ? t("Velg organisasjon") : step === "password" ? t("Logg inn") : step === "reset" ? t("Tilbakestill passord") : t("Arrangør")}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {orgChoices
                ? t("E-posten din er knyttet til flere organisasjoner. Velg hvilken du vil logge inn som.")
                : step === "password"
                ? t("Skriv inn passordet ditt for å fortsette.")
                : step === "reset"
                ? t("Sett et nytt passord med koden vi har sendt deg.")
                : t("Skriv inn e-posten din. Har du konto, logger du inn – hvis ikke, oppretter vi en ny.")}
            </p>
          </CardHeader>
          <CardContent>
            {orgChoices ? (
              <div className="space-y-3" data-testid="login-org-picker">
                {orgChoices.map((o) => (
                  <button
                    key={o.id}
                    onClick={() => chooseOrg(o.id)}
                    disabled={submitting}
                    className="w-full rounded-xl border border-border bg-card hover:bg-secondary transition-colors duration-150 p-4 flex items-center justify-between gap-3 text-left"
                    data-testid={`login-org-choice-${o.id}`}
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      <span className="w-9 h-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                        <Building2 className="w-4 h-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-medium truncate">{o.org_name}</span>
                        <span className="block text-xs text-muted-foreground">{t("Org.nr {n}", { n: o.org_number })}</span>
                      </span>
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </button>
                ))}
                <Button variant="ghost" className="w-full" onClick={() => setOrgChoices(null)} data-testid="login-org-back-button">
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  {t("Tilbake")}
                </Button>
              </div>
            ) : step === "email" ? (
            <>
            <form onSubmit={handleEmailContinue} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="login-email">{t("E-post")}</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="din@epost.no"
                  required
                  autoFocus
                  data-testid="login-email-input"
                />
              </div>
              <Button type="submit" className="w-full h-11" disabled={submitting} data-testid="login-continue-button">
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("Sjekker...")}</>) : (<>{t("Fortsett")}<ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            </form>
            <p className="text-sm text-muted-foreground text-center mt-6">
              {t("Ny arrangør?")}{" "}
              <Link to="/registrer" className="font-medium text-foreground underline underline-offset-4" data-testid="login-register-link">
                {t("Registrer organisasjonen din")}
              </Link>
            </p>
            </>
            ) : step === "reset" ? (
            <>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("Vi har sendt en 6-sifret kode til")} <span className="font-medium text-foreground">{email}</span>. {t("Skriv den inn og velg nytt passord.")}
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="reset-code">{t("Kode fra e-post")}</Label>
                <Input
                  id="reset-code"
                  inputMode="numeric"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  placeholder="123456"
                  required
                  autoFocus
                  data-testid="reset-code-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reset-password">{t("Nytt passord")}</Label>
                <Input
                  id="reset-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t("Minst 8 tegn")}
                  required
                  minLength={8}
                  data-testid="reset-password-input"
                />
              </div>
              <Button type="submit" className="w-full h-11" disabled={submitting} data-testid="reset-submit-button">
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("Oppdaterer...")}</>) : t("Sett nytt passord")}
              </Button>
              <div className="rounded-lg border border-border bg-secondary/40 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="reset-resend-box">
                <p className="text-xs text-muted-foreground" data-testid="reset-resend-hint">
                  {t("Fikk du ingen kode? Sjekk søppelposten, eller be om en ny.")}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="shrink-0"
                  onClick={resendResetCode}
                  disabled={resendWait > 0 || resending}
                  aria-live="polite"
                  data-testid="reset-resend-button"
                >
                  {resending ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <RotateCw className="w-4 h-4 mr-1.5" />
                  )}
                  {resendWait > 0 ? t("Send ny kode ({s} s)", { s: resendWait }) : t("Send ny kode")}
                </Button>
              </div>
              <Button type="button" variant="ghost" className="w-full" onClick={() => setStep("password")} data-testid="reset-back-button">
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                {t("Tilbake til innlogging")}
              </Button>
            </form>
            </>
            ) : (
            <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("E-post")}</Label>
                <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/50 px-3 py-2">
                  <span className="text-sm truncate" data-testid="login-email-display">{email}</span>
                  <button
                    type="button"
                    onClick={() => { setStep("email"); setPassword(""); }}
                    className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors duration-150 shrink-0 ml-3"
                    data-testid="login-change-email-button"
                  >
                    {t("Endre")}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="login-password">{t("Passord")}</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="********"
                  required
                  autoFocus
                  data-testid="login-password-input"
                />
              </div>
              <Button type="submit" className="w-full h-11" disabled={submitting} data-testid="login-submit-button">
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("Logger inn...")}</>) : t("Logg inn")}
              </Button>
              <button
                type="button"
                onClick={startForgotPassword}
                disabled={submitting}
                className="w-full text-center text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors duration-150 pt-1"
                data-testid="login-forgot-button"
              >
                {t("Glemt passord?")}
              </button>
            </form>
            </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
